import { Paperclip, X } from "lucide-react";
import React, { useState, useRef } from "react";
import { Button, Group, Text, TextInput } from "@mantine/core";
import axios from "axios";
import useSecureAnnouncementUploadApiService from "../services/api/SecureAnnouncementUploadApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import {
  getMediaType,
  ACCEPT_EXTENSIONS,
  ANNOUNCEMENT_IMAGE_FILE_EXTENSION,
  ANNOUNCEMENT_ACCEPT_IMAGE_FILE_SIZE,
  ANNOUNCEMENT_ACCEPT_FILE_SIZE,
} from "../constants/MediaTypes";
import { getFileExtension } from "../../../utils/File";

interface SecureAnnouncementUploadProps {
  onSuccess: (fileData: {
    url: string;
    mediaType: string;
    sizeKB: number;
    fileName: string;
  }) => void;
  onRemove: () => void;
  disabled?: boolean;
  currentFileName?: string;
}

export const SecureAnnouncementUpload: React.FC<
  SecureAnnouncementUploadProps
> = ({ onSuccess, onRemove, disabled = false, currentFileName }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>(
    currentFileName
  );
  const { showToast } = useToasts();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { mutateAsync: generateSignedUrl, isLoading: isGeneratingUrl } =
    useSecureAnnouncementUploadApiService();

  const validateFileSize = (file: File): string | null => {
    const fileType = file.type;

    // Check image size (5MB limit)
    if (ANNOUNCEMENT_IMAGE_FILE_EXTENSION.includes(fileType)) {
      if (file.size > ANNOUNCEMENT_ACCEPT_IMAGE_FILE_SIZE) {
        return "Image file size exceeds the limit of 5MB";
      }
    }
    // Check other file types (16MB limit)
    else if (file.size > ANNOUNCEMENT_ACCEPT_FILE_SIZE) {
      return `File size exceeds the limit of 16MB`;
    }

    return null;
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const { currentTarget } = event;
    const { files } = currentTarget;

    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file size
    const sizeError = validateFileSize(file);
    if (sizeError) {
      showToast({
        strapiId: "announcement_file_upload_failed",
        data: { message: sizeError },
      });
      currentTarget.value = "";
      return;
    }

    const fileName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
    const fileExtension = getFileExtension(file.name).toLowerCase();

    setUploading(true);

    try {
      // Get the signed URL from backend
      const response = await generateSignedUrl({
        file_name: fileName,
        file_extension: fileExtension,
        upload_type: "announcement",
      });

      // Upload the file directly to GCS using the signed URL
      const unsignedAxios = axios.create();
      await unsignedAxios.put(response.signed_url, file, {
        headers: {
          "Content-Type": response.content_type,
          "Content-Length": file.size.toString(),
          "X-Goog-Content-Length-Range": `${response.min_size},${response.max_size}`,
        },
      });

      // Success! Store file name and notify parent
      setUploadedFileName(file.name);
      onSuccess({
        url: response.file_path,
        mediaType: getMediaType(file.type),
        sizeKB: Math.round(file.size / 1024), // Convert bytes to KB
        fileName: file.name,
      });

      showToast({ strapiId: "announcement_file_uploaded" });
    } catch (error) {
      let errorMessage = "Upload failed";
      if (axios.isAxiosError(error)) {
        errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          (error.response
            ? `Upload failed: ${error.response.status} ${
                error.response.statusText || ""
              }`.trim()
            : error.message || errorMessage);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      showToast({
        strapiId: "announcement_file_upload_failed",
        data: { message: errorMessage },
      });
    } finally {
      setUploading(false);
      currentTarget.value = "";
    }
  };

  const handleRemove = (): void => {
    setUploadedFileName(undefined);
    onRemove();
  };

  const isLoading = uploading || isGeneratingUrl;

  const getButtonText = (): string => {
    if (isLoading) return "Uploading...";
    if (uploadedFileName) return "Remove";
    return "Attach";
  };

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
        accept={ACCEPT_EXTENSIONS}
        disabled={disabled || isLoading}
      />

      <Group position="apart" spacing="md">
        {uploadedFileName && (
          <Group>
            <TextInput
              placeholder="File name"
              icon={<Paperclip size={14} />}
              value={uploadedFileName}
              disabled
              readOnly
            />
          </Group>
        )}
        <Button
          leftIcon={
            uploadedFileName ? <X size={22} /> : <Paperclip size={22} />
          }
          onClick={
            uploadedFileName
              ? handleRemove
              : () => fileInputRef.current?.click()
          }
          color="jem-theme"
          loading={isLoading}
          disabled={disabled || isLoading}
        >
          {getButtonText()} Media
        </Button>
      </Group>

      {!uploadedFileName && (
        <Text size="xs" color="dimmed" mt="xs">
          Supports PDF, images (JPG, PNG), videos (MP4), and audio (MP3, OGG,
          AMR).
          <br />
          Max 5MB for images, 16MB for other files.
        </Text>
      )}
    </div>
  );
};

export default SecureAnnouncementUpload;
